import { useState } from 'react';
import { motion } from 'framer-motion';
import { Phone, Mail, MapPin, Clock, Send, MessageSquare } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { toast } from 'sonner';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const contactInfo = [
  {
    icon: Phone,
    title: 'Phone',
    value: '912-388-6305',
    description: 'Call or text anytime',
    href: 'tel:912-388-6305',
    color: 'bg-primary/10 text-primary',
  },
  {
    icon: Mail,
    title: 'Email',
    value: 'care@lifebreathcpr.com',
    description: 'We respond within 24 hours',
    href: 'mailto:care@lifebreathcpr.com',
    color: 'bg-[#83C5BE]/20 text-[#006D77]',
  },
  {
    icon: MapPin,
    title: 'Service Area',
    value: 'Georgia',
    description: 'Mobile services statewide',
    color: 'bg-[#E29578]/20 text-[#E29578]',
  },
];

export default function Contact() {
  const [form, setForm] = useState({
    name: '',
    email: '',
    phone: '',
    subject: '',
    message: '',
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    
    try {
      await axios.post(`${API}/contact`, form);
      toast.success('Message sent!', {
        description: 'We will get back to you as soon as possible.',
      });
      setForm({
        name: '',
        email: '',
        phone: '',
        subject: '',
        message: '',
      });
    } catch (error) {
      toast.error('Failed to send message', {
        description: 'Please try again or call us directly.',
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-white">
      {/* Hero */}
      <section className="bg-gradient-to-br from-[#EDF6F9] via-white to-[#83C5BE]/20 py-16 md:py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-primary/10 text-primary text-sm font-medium mb-6">
              <MessageSquare className="w-4 h-4" />
              <span>Get in Touch</span>
            </div>
            <h1 
              className="text-4xl md:text-5xl font-bold text-[#264653] mb-6"
              style={{ fontFamily: 'Manrope, sans-serif' }}
              data-testid="contact-title"
            >
              Contact Us
            </h1>
            <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
              Have questions? We'd love to hear from you. Send us a message or give us a call.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Contact Info Cards */}
      <section className="py-16 md:py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-3 gap-6 mb-12">
            {contactInfo.map((info, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
              >
                <Card className="h-full text-center border-slate-100 hover:shadow-lg transition-all" data-testid={`contact-info-${index}`}>
                  <CardContent className="p-8">
                    <div className={`w-14 h-14 rounded-xl ${info.color} flex items-center justify-center mx-auto mb-4`}>
                      <info.icon className="w-7 h-7" />
                    </div>
                    <h3 
                      className="text-lg font-semibold text-[#264653] mb-1"
                      style={{ fontFamily: 'Manrope, sans-serif' }}
                    >
                      {info.title}
                    </h3>
                    {info.href ? (
                      <a href={info.href} className="text-xl font-bold text-primary hover:underline block mb-1">
                        {info.value}
                      </a>
                    ) : (
                      <p className="text-xl font-bold text-[#264653] mb-1">{info.value}</p>
                    )}
                    <p className="text-sm text-muted-foreground">{info.description}</p>
                  </CardContent>
                </Card>
              </motion.div>
            ))}
          </div>

          {/* Office Hours */}
          <div className="grid md:grid-cols-2 gap-6 mb-16">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5 }}
            >
              <Card className="h-full border-slate-100" data-testid="notary-hours">
                <CardHeader>
                  <CardTitle className="text-xl text-[#264653] flex items-center gap-2" style={{ fontFamily: 'Manrope, sans-serif' }}>
                    <Clock className="w-5 h-5 text-[#E29578]" />
                    Mobile Notary Office Hours
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-2 text-muted-foreground">
                  <p><strong className="text-[#264653]">Monday – Friday:</strong> 9:00 AM – 6:00 PM</p>
                  <p><strong className="text-[#264653]">Saturday:</strong> 10:00 AM – 4:00 PM</p>
                  <p><strong className="text-[#264653]">Sunday:</strong> By Appointment Only</p>
                  <p className="text-sm pt-2 italic">After-hours, evening, and emergency appointments available upon request.</p>
                </CardContent>
              </Card>
            </motion.div>
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: 0.1 }}
            >
              <Card className="h-full border-slate-100" data-testid="cpr-hours">
                <CardHeader>
                  <CardTitle className="text-xl text-[#264653] flex items-center gap-2" style={{ fontFamily: 'Manrope, sans-serif' }}>
                    <Clock className="w-5 h-5 text-primary" />
                    CPR & First Aid Training Hours
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-2 text-muted-foreground">
                  <p><strong className="text-[#264653]">Monday – Friday:</strong> 9:00 AM – 7:00 PM</p>
                  <p><strong className="text-[#264653]">Saturday:</strong> 9:00 AM – 5:00 PM</p>
                  <p><strong className="text-[#264653]">Sunday:</strong> Group & Church Training by Appointment</p>
                </CardContent>
              </Card>
            </motion.div>
          </div>

          {/* Contact Form */}
          <div className="grid lg:grid-cols-2 gap-12 items-start">
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5 }}
            >
              <h2 
                className="text-3xl md:text-4xl font-bold text-[#264653] mb-6"
                style={{ fontFamily: 'Manrope, sans-serif' }}
              >
                Send Us a Message
              </h2>
              <p className="text-muted-foreground mb-8 leading-relaxed">
                Fill out the form and we'll get back to you as soon as possible. 
                For immediate assistance, please call or text us directly.
              </p>
              <div className="bg-[#EDF6F9] rounded-2xl p-8">
                <h3 
                  className="font-semibold text-[#264653] mb-4"
                  style={{ fontFamily: 'Manrope, sans-serif' }}
                >
                  Quick Contact
                </h3>
                <a 
                  href="tel:912-388-6305" 
                  className="flex items-center gap-3 text-primary font-bold text-2xl hover:underline"
                >
                  <div className="w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center">
                    <Phone className="w-6 h-6 text-primary" />
                  </div>
                  912-388-6305
                </a>
                <p className="text-muted-foreground mt-2 ml-15">Call or text anytime</p>
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: 0.1 }}
            >
              <Card className="border-slate-100" data-testid="contact-form">
                <CardHeader>
                  <CardTitle className="text-2xl text-[#264653]" style={{ fontFamily: 'Manrope, sans-serif' }}>
                    Contact Form
                  </CardTitle>
                  <CardDescription>
                    We'll respond within 24 hours.
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <form onSubmit={handleSubmit} className="space-y-6">
                    <div className="grid md:grid-cols-2 gap-6">
                      <div className="space-y-2">
                        <Label htmlFor="name">Name *</Label>
                        <Input
                          id="name"
                          data-testid="contact-name-input"
                          value={form.name}
                          onChange={(e) => setForm({ ...form, name: e.target.value })}
                          required
                          placeholder="John Doe"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="email">Email *</Label>
                        <Input
                          id="email"
                          type="email"
                          data-testid="contact-email-input"
                          value={form.email}
                          onChange={(e) => setForm({ ...form, email: e.target.value })}
                          required
                          placeholder="john@example.com"
                        />
                      </div>
                    </div>
                    <div className="grid md:grid-cols-2 gap-6">
                      <div className="space-y-2">
                        <Label htmlFor="phone">Phone</Label>
                        <Input
                          id="phone"
                          type="tel"
                          data-testid="contact-phone-input"
                          value={form.phone}
                          onChange={(e) => setForm({ ...form, phone: e.target.value })}
                          placeholder="912-388-6305"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="subject">Subject *</Label>
                        <Input
                          id="subject"
                          data-testid="contact-subject-input"
                          value={form.subject}
                          onChange={(e) => setForm({ ...form, subject: e.target.value })}
                          required
                          placeholder="CPR Training Inquiry"
                        />
                      </div>
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="message">Message *</Label>
                      <Textarea
                        id="message"
                        data-testid="contact-message-input"
                        value={form.message}
                        onChange={(e) => setForm({ ...form, message: e.target.value })}
                        required
                        placeholder="Tell us how we can help..."
                        rows={5}
                      />
                    </div>
                    <Button 
                      type="submit" 
                      size="lg"
                      className="w-full rounded-full bg-primary hover:bg-primary/90"
                      disabled={isSubmitting}
                      data-testid="contact-submit-btn"
                    >
                      {isSubmitting ? 'Sending...' : 'Send Message'}
                      <Send className="ml-2 w-5 h-5" />
                    </Button>
                  </form>
                </CardContent>
              </Card>
            </motion.div>
          </div>
        </div>
      </section>
    </div>
  );
}
